function ordinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return 'th';
  }

  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function formatTime(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}.${minutes}`;
}

export function formatSupportAdminDateTime(value?: string | Date | null): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  const time = formatTime(date);
  const today = startOfDay(new Date());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dateStart = startOfDay(date);

  if (dateStart.getTime() === today.getTime()) {
    return `Today ${time}`;
  }

  if (dateStart.getTime() === yesterday.getTime()) {
    return `Yesterday ${time}`;
  }

  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'long' });
  const year = String(date.getFullYear()).slice(-2);

  return `${day}${ordinalSuffix(day)} ${month} ${year} • ${time}`;
}

export function formatSupportDurationMs(ms?: number | null): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) {
    return '—';
  }

  const totalMinutes = Math.round(ms / 60000);
  if (totalMinutes < 1) {
    return '<1m';
  }

  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  return `${minutes}m`;
}

/** Formats a millisecond duration as "3 hours 41 minutes" for user-facing support copy. */
export function formatAvgResponseTime(ms: number): string {
  const totalMinutes = Math.max(0, Math.round(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const hourLabel = hours === 1 ? '1 hour' : `${hours} hours`;
  const minuteLabel = minutes === 1 ? '1 minute' : `${minutes} minutes`;

  if (hours === 0) {
    return minuteLabel;
  }
  if (minutes === 0) {
    return hourLabel;
  }
  return `${hourLabel} ${minuteLabel}`;
}
